import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthProvider';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LandingPage } from './pages/LandingPage';
import { Login } from './pages/Login';
import { SignUp } from './pages/SignUp';
import { EnquiryForm } from './pages/EnquiryForm';
import { VerifyEmail } from './pages/VerifyEmail';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { AcceptInvite } from './pages/AcceptInvite';
import { PatientPicker } from './pages/PatientPicker';
import { PatientDisplay } from './views/patient/PatientDisplay';
import { CarerLogin } from './views/carer/CarerLogin';
import { CarerDashboard } from './views/carer/CarerDashboard';
import { FamilyLogin } from './views/family/FamilyLogin';
import { FamilyFeed } from './views/family/FamilyFeed';
import { AdminDashboard } from './views/admin/AdminDashboard';
import { FAQ } from './views/faq/FAQ';
import { Onboarding } from './views/onboarding/Onboarding';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/enquiry" element={<EnquiryForm />} />
          <Route path="/login" element={<Login />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Protected routes */}
          <Route path="/patient" element={<ProtectedRoute><PatientPicker /></ProtectedRoute>} />
          <Route path="/patient/:id" element={<ProtectedRoute><PatientDisplay /></ProtectedRoute>} />
          <Route path="/carer" element={<ProtectedRoute><CarerLogin /></ProtectedRoute>} />
          <Route path="/carer/dashboard" element={<ProtectedRoute><CarerDashboard patientId={parseInt(import.meta.env.VITE_PATIENT_ID || '1', 10)} /></ProtectedRoute>} />
          <Route path="/family" element={<ProtectedRoute><FamilyLogin /></ProtectedRoute>} />
          <Route path="/family/feed" element={<ProtectedRoute><FamilyFeed patientId={parseInt(import.meta.env.VITE_PATIENT_ID || '1', 10)} /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;